import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import usePageTitle from "hooks/usePageTitle";
import SuccessNotification from "messages/SuccessNotification";
import { useState, useEffect } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import resources from "assets/icons/resources.png";
import blogImage from "assets/icons/blog.png";
import author from "assets/icons/author-default.png";
import { Button, Link } from "@mui/material";

interface Blog {
    id: number;
    title: string;
    slug: string;
    layout?: { blockType: string; columns: any[] }[];
    createdAt?: string;
    updatedAt?: string;
    _status?: string;
    meta?: { title: string | null; image: string | null; description: string | null };
    slugLock?: boolean;
    featuredImage: { sizes: { thumbnail: { url: string } } };
    author: {
        id: number;
        name: string;
        Image: { sizes: { thumbnail: { url: string } } };
    };
    categories: {
        id: number;
        title: string;
    }[];
}


const Blogs = () => {
    const [success, setSuccess] = useState(false);
    const [blogs, setBlogs] = useState<Blog[]>([]);
    const [visibleBlogs, setVisibleBlogs] = useState(9);
    const [loading, setLoading] = useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const cmsUrl = process.env.REACT_APP_CMS_API_URL;

    usePageTitle("Blog");
    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                // Use the exact URL that works in the browser
                const response = await axios.get(`${process.env.REACT_APP_CMS_API_URL}/api/blogs`);
                setBlogs(response.data.docs);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching blogs:", error);
                setLoading(false);
            }
        };
        fetchBlogs();
    }, []);

    const handleShowMore = () => {
        setVisibleBlogs((prev) => prev + 3);
    };

    console.log('blogs', blogs);

    return (
        <Box
            sx={{
                mt: { sm: 5, md: 5 },
                mb: 25,
                maxWidth: "1210px",
                mx: "auto",
                px: isMobile ? 3 : 0,
            }}
        >
            {success && SuccessNotification("Your message was sent successfully.")}

            <Stack>
                <img src={resources} style={{ width: "52px", marginBottom: "25px" }} alt="lmp logo" />
                <Typography
                    variant="h3"
                    align="left"
                    sx={{ textAlign: "left", mb: 3, fontWeight: "bold" }}
                >
                    Resources
                </Typography>
                <Typography
                    color="#080A52"
                    whiteSpace="pre-line"
                    sx={{
                        fontFamily: "Neue Haas Grotesk Display medium",
                        fontWeight: "450",
                        fontSize: "18px",
                        lineHeight: "22.5px",
                    }}
                >
                    Whether you've just been in a collision or you’re trying to make sense of your next steps, you’re in the right place. This blog is packed with straightforward legal guidance, real-world advice, and insider tips from our expert CrashLaw24 team. We break down complex legal jargon into clear, simple language so you know your rights, what to expect, and how we can help, anytime day or night. 
                </Typography>
            </Stack>

            {loading && <Typography>Loading blogs...</Typography>}

            {!loading && blogs.length === 0 && (
                <Typography>No blog posts found.</Typography>
            )}
            {!loading && blogs.length > 0 && (
                <Stack spacing={3} sx={{ mt: 8 }}>
                    <Box
                        sx={{
                            display: "grid",
                            gap: "30px",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                                md: "repeat(3, 1fr)",
                            },
                        }}
                    >
                        {blogs.slice(0, visibleBlogs).map((blog) => {
                            const imageUrl = blog.featuredImage?.sizes?.thumbnail?.url
                                ? `${blog.featuredImage.sizes.thumbnail.url}`
                                : blogImage;

                            const authorUrl = blog.author?.Image?.sizes.thumbnail?.url
                                ? `${blog.author?.Image?.sizes.thumbnail?.url}`
                                : author;

                            const encodedImageUrl = encodeURI(imageUrl);
                            console.log(encodedImageUrl);
                            const encodedAuthorImageUrl = encodeURI(authorUrl);

                            // Format createdAt to blogDate
                            const createdAt = blog.createdAt;
                            let blogDate = '';
                            if (createdAt) {
                                const date = new Date(createdAt);
                                blogDate = date.toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'long',
                                    year: 'numeric',
                                });
                            }

                            return (
                                <Box key={blog.id} sx={{ backgroundColor: "#f0f0f0", borderRadius: "15px" }}>
                                    <Box
                                        sx={{
                                            backgroundImage: `url(${encodedImageUrl})`,
                                            backgroundRepeat: "no-repeat",
                                            backgroundPosition: "center",
                                            backgroundSize: "cover",
                                            minHeight: "250px",
                                            borderTopLeftRadius: "15px",
                                            borderTopRightRadius: "15px",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", justifyContent: "flex-end", marginRight: "35px", gap: "10px" }}>
                                            {blog.categories?.map((category, index) => (
                                                <Typography
                                                    key={index}
                                                    sx={{
                                                        backgroundColor: category.title === "Featured" ? "#E02642" : "#11BA55",
                                                        color: "#fff",
                                                        borderBottomLeftRadius: "5px",
                                                        borderBottomRightRadius: "5px",
                                                        px: 2,
                                                        py: 0.5,
                                                        fontSize: "18px",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    {category.title}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                    <Box sx={{ padding: "20px 25px 35px" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
                                            <div>
                                                <img style={{ maxWidth: "50px", borderRadius: "100%", objectFit: "contain" }} src={encodedAuthorImageUrl} alt="" />
                                            </div>
                                            <div>
                                                {/* {blog.author?.name && (
                                                    <Typography sx={{ fontWeight: "700", fontSize: "16px", textAlign: "left", lineHeight: "22px", marginBottom: "5px", color: "#080A52" }}>{blog.author?.name}</Typography>
                                                )} */}
                                                <Typography
                                                    sx={{
                                                        fontFamily: "Neue Haas Grotesk Display medium",
                                                        fontWeight: "450",
                                                        fontSize: "16px",
                                                        lineHeight: "22.5px",
                                                    }}
                                                >
                                                    {blogDate}
                                                    {blog.categories?.length > 0 && (
                                                        <>
                                                            &nbsp;&nbsp;|&nbsp;&nbsp;
                                                            {blog.categories.map((category, index) => (
                                                                <span key={category.id}>
                                                                    {category.title}
                                                                    {index < blog.categories.length - 1 ? ", " : ""}
                                                                </span>
                                                            ))}
                                                        </>
                                                    )}
                                                </Typography>

                                            </div>
                                        </Box>
                                        <Typography sx={{ fontWeight: "700", fontSize: "22px", textAlign: "left", lineHeight: "26px", marginBottom: "25px", color: "#080A52", mt: 2 }}>{blog.title}</Typography>
                                        <Button
                                            href={`/blogs/${blog.slug}`}
                                            variant="contained"
                                            component={Link}
                                            sx={{
                                                fontWeight: '700',
                                                borderRadius: 28,
                                                margin: 0,
                                                px: 2,
                                                py: 1,
                                                color: "#fff",
                                                textTransform: "none",
                                                width: "116px",
                                                boxShadow: "4px 4px 8px 0px rgba(0, 0, 0, 0.2)",
                                            }}
                                        >
                                            Read now
                                        </Button>
                                    </Box>
                                </Box>
                            );
                        })}

                    </Box>
                    {visibleBlogs < blogs.length && (
                        <Box sx={{ textAlign: "center", mt: 12 }}>
                            <Typography
                                onClick={handleShowMore}
                                sx={{
                                    fontFamily: "Neue Haas Grotesk Display medium",
                                    fontWeight: "450",
                                    fontSize: "16px",
                                    lineHeight: "22.5px",
                                    textDecoration: "underline",
                                    cursor: "pointer"
                                }}
                            >
                                Show More
                            </Typography>
                        </Box>
                    )}
                </Stack>
            )}
        </Box>
    );
};

export default Blogs;
